import React, { useEffect, useState, useMemo } from 'react';

// custom components //
import { CustomSpinner } from "_atoms";

// bootstrap
import Col from 'react-bootstrap/Col';
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/esm/Button";
import Row from 'react-bootstrap/Row';

// select
import Select from "react-select";

// redux
import { useSelector, useDispatch } from 'react-redux';
import {
    createTask,
    selectCreateTaskStatus,
    selectCreateTaskError,
    clearCreateTaskStatus,
    selectUpdateTaskStatus,
    updateTask,
    resetUpdateTaskFlags,
} from "_redux/slices/timeline/timeline-data";
import { selectSelectedTimeline, selectPickUpLocations } from "_redux/slices/timeline/timeline-areas";
import { selectAdhocTaskEmployee, selectSelectedAdhocTask, resetAdhocTaskFlags, setAdhocTaskEmployee } from '_redux/slices/timeline/selected-task';
import { selectTimelineDate, selectDefaultAdhocLocation } from '_redux/slices/timeline/timeline-control';
import { selectAllEmployees, sendEmployeeNotification } from '_redux/slices/timeline/employees';

// custom components
import EmployeeProfilePicture from '../employee-components/employee-profile-picture';
import CustomDivider from '_atoms/lines/custom-divider';

// icons
import { PiNotepadFill } from "react-icons/pi";
import { FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

// styles
import { Spacing, Colors } from '_styles'

// constants
import { EmployeeInformationColumnWidth } from '../timeline-constants';

// date
import moment from 'moment';

// functions

import { selectFirebaseInfo, selectFirebaseUser } from '_redux/slices/main-screen/user';

// adhoc task title options
const ADHOC_TASK_OPTIONS = [
    { value: "Go to Location", label: "Go to Location", description: "Please proceed to the assigned location." },
    { value: "Wheelchair Collection", label: "Wheelchair Collection", description: "Please collect the wheelchair passenger and assist them to their destination." },
    { value: "Break Cover", label: "Break Cover", description: "Please provide break cover for the assigned area." },
    { value: "Help Pax", label: "Help Pax", description: "Please assist the passenger with their needs." },
    { value: "Office", label: "Office", description: "Please report to the office." },
    { value: "Fuelling", label: "Fuelling", description: "Please proceed to assist with aircraft fuelling." },
    { value: "Trolleys", label: "Trolleys", description: "Please assist with trolley management." },
    { value: "General Adhoc", label: "General Adhoc", description: "" },
];

const CreateAdhocTask = (props) => {
    //#region redux
    const dispatch = useDispatch();
    const selectedTimeline = useSelector(selectSelectedTimeline);
    const createTaskStatus = useSelector(selectCreateTaskStatus);
    const adhocTaskEmployee = useSelector(selectAdhocTaskEmployee);                       // selected employee to add adhoc task
    const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date
    const selectedAdhocTask = useSelector(selectSelectedAdhocTask);                       // select adhoc task to edit
    const updateTaskStatus = useSelector(selectUpdateTaskStatus);                         // update task status
    const createTaskError = useSelector(selectCreateTaskError);                            // create task error
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user (db record)
    const firebaseUser = useSelector(selectFirebaseUser);                                 // firebase auth user (uid lives here)
    const allEmployees = useSelector(selectAllEmployees);                                 // list of all employees
    const locations = useSelector(selectPickUpLocations);                                 // airport locations
    const defaultAdhocLocationId = useSelector(selectDefaultAdhocLocation);              // default adhoc location
    //#endregion

    //#region local states
    const [assignedTime, setAssignedTime] = useState(() => {
        const now = new Date();
        const minutes = Math.round(now.getMinutes() / 5) * 5;
        const rounded = new Date(now);
        rounded.setMinutes(minutes, 0, 0);
        return `${String(rounded.getHours()).padStart(2, '0')}:${String(rounded.getMinutes()).padStart(2, '0')}`;
    })
    const [title, setTitle] = useState(() => {
        if (!defaultAdhocLocationId) return "";
        const defaultLoc = locations?.find(loc => loc._id === defaultAdhocLocationId);
        return defaultLoc ? `Go to ${defaultLoc.title || defaultLoc.name}` : "Go to Location";
    })  // adhoc task title
    const [description, setDescription] = useState("")                               // adhoc task description
    const [selectedLocation, setSelectedLocation] = useState(() =>
        locations?.find(loc => loc._id === defaultAdhocLocationId) || null
    );
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    //#endregion

    //#region useEffect

    const timeIntervalOptions = useMemo(() => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            for (let minute = 0; minute < 60; minute += 5) {
                options.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
            }
        }
        return options;
    }, []);

    // Reset stale status when the form mounts so a previous submission doesn't bleed in
    useEffect(() => {
        dispatch(clearCreateTaskStatus());
        dispatch(resetUpdateTaskFlags());
    }, []);

    useEffect(() => {
        if (createTaskStatus == "succeeded" && adhocTaskEmployee?.FCMTokens) {
            dispatch(sendEmployeeNotification({
                employeeId: adhocTaskEmployee?.employeeId,
                notificationTitle: "New Adhoc Task at " + assignedTime,
                notificationText: title,
                fcmToken: adhocTaskEmployee.FCMTokens,
            }));
            setShowSuccess(true);
            setShowError(false);
        }
        if (createTaskStatus == "failed") {
            setShowError(true);
            setShowSuccess(false);
        }
        if (createTaskStatus == "succeeded" || updateTaskStatus == "succeeded") {
            const timer = setTimeout(() => {
                dispatch(resetAdhocTaskFlags());
                dispatch(clearCreateTaskStatus());
                dispatch(resetUpdateTaskFlags());
                props.onClose();
            }, 1500);
            return () => clearTimeout(timer);
        }

        // Safety net: if loading for more than 10 seconds, reset to avoid being permanently stuck
        if (createTaskStatus == "loading" || updateTaskStatus == "loading") {
            const safetyTimer = setTimeout(() => {
                dispatch(clearCreateTaskStatus());
                dispatch(resetUpdateTaskFlags());
                setShowError(true);
            }, 10000);
            return () => clearTimeout(safetyTimer);
        }
    }, [createTaskStatus, updateTaskStatus]);

    // check if there is a selected adhoc task to edit
    useEffect(() => {
        if (!Object.isObjectEmpty(selectedAdhocTask)) {
            const existingTitle = selectedAdhocTask?.adhocTaskInformation?.title;
            const existingDescription = selectedAdhocTask?.adhocTaskInformation?.description;
            const existingTime = selectedAdhocTask?.assignedTime;
            const existingLocation = selectedAdhocTask?.location || null;

            setTitle(existingTitle || "");
            setDescription(existingDescription || "");
            setAssignedTime(existingTime || "");
            setSelectedLocation(existingLocation);
        } else {
            const defaultLoc = locations?.find(loc => loc._id === defaultAdhocLocationId) || null;
            const locationName = defaultLoc ? (defaultLoc.title || defaultLoc.name) : null;
            const defaultTitle = defaultAdhocLocationId
                ? (locationName ? `Go to ${locationName}` : "Go to Location")
                : "";
            setTitle(defaultTitle);
            setDescription(defaultTitle);
            const now = new Date();
            const minutes = Math.round(now.getMinutes() / 5) * 5;
            const rounded = new Date(now);
            rounded.setMinutes(minutes, 0, 0);
            setAssignedTime(`${String(rounded.getHours()).padStart(2, '0')}:${String(rounded.getMinutes()).padStart(2, '0')}`);
            setSelectedLocation(defaultLoc);
        }
    }, [selectedAdhocTask, defaultAdhocLocationId, locations])

    //#endregion

    //#region functions

    // create adhoc task
    const createAdhocTask = () => {
        // Guard against partially completed forms so we never dispatch malformed payloads.
        if (!assignedTime || !title || !adhocTaskEmployee) {
            return;
        }

        const timestamp = new Date().getTime();
        const sanitizedLocation = selectedLocation
            ? { _id: selectedLocation._id, title: selectedLocation.title || null, name: selectedLocation.name || null }
            : null;
        const employeeName = adhocTaskEmployee?.title || adhocTaskEmployee?.name || null;
        const allocatorName = dbUser?.firstName || dbUser?.name || firebaseUser?.displayName || firebaseUser?.email || null;
        let taskObject = {
            _id: `${adhocTaskEmployee?.employeeId}-adhocTask-${timestamp}`,
            isAdhocTask: true,
            active: false,
            completed: false,
            cancelled: false,
            pending: false,
            employeeId: adhocTaskEmployee?.employeeId,
            employeeName: employeeName,
            assignedTime: assignedTime,
            allocatedTime: new Date(),
            allocatedBy: firebaseUser?.uid || null,
            allocatedByName: allocatorName,
            iata: dbUser.details.authority,
            location: sanitizedLocation,
            selectedTimeline: selectedTimeline?._id || null,
            adhocTaskInformation: {
                title: title,
                description: description,
                assignedDate: moment(new Date()).format().split("T")[0],
                airport: dbUser.details.authority
            }
        };

        console.log('[Adhoc Task Create] Creating task:', taskObject);
        console.log('[Adhoc Task Create] Task ID:', taskObject._id);
        console.log('[Adhoc Task Create] Employee:', adhocTaskEmployee?.title, adhocTaskEmployee?.employeeId);
        console.log('[Adhoc Task Create] Title:', title);
        console.log('[Adhoc Task Create] Assigned Time:', assignedTime);
        console.log('[Adhoc Task Create] Assigned Date:', taskObject.adhocTaskInformation.assignedDate);
        console.log('[Adhoc Task Create] Selected Timeline:', selectedTimeline);
        dispatch(createTask(taskObject));
    }

    // update adhoc task
    const editAdhocTask = () => {
        const sanitizedLocation = selectedLocation
            ? { _id: selectedLocation._id, title: selectedLocation.title || null, name: selectedLocation.name || null }
            : null;
        let taskUpdates = {
            id: selectedAdhocTask._id,
            assignedTime: assignedTime,
            location: sanitizedLocation,
            adhocTaskInformation: {
                ...selectedAdhocTask?.adhocTaskInformation,
                title: title,
                description: description,
            }
        }
        console.log('[Adhoc Task Edit] Updating task:', taskUpdates);
        console.log('[Adhoc Task Edit] Task ID:', taskUpdates.id);
        console.log('[Adhoc Task Edit] New Title:', title);
        console.log('[Adhoc Task Edit] New Assigned Time:', assignedTime);
        dispatch(updateTask(taskUpdates))
    }

    // handle title selection and auto-populate description
    const handleTitleChange = (selectedOption) => {
        if (selectedOption) {
            if (selectedOption.value === "Go to Location") {
                const locationName = selectedLocation ? (selectedLocation.title || selectedLocation.name) : null;
                setTitle(locationName ? `Go to ${locationName}` : "Go to Location");
                setDescription(locationName ? `Go to ${locationName}` : "Go to Location");
            } else {
                setTitle(selectedOption.value);
                setDescription(selectedOption.description || "");
            }
        } else {
            setTitle("");
            setDescription("");
        }
    }

    // handle location selection and update title + description if Go to Location is active
    const handleLocationChange = (opt) => {
        const loc = opt ? opt.value : null;
        setSelectedLocation(loc);
        if (title.startsWith("Go to")) {
            const locationName = loc ? (loc.title || loc.name) : null;
            setTitle(locationName ? `Go to ${locationName}` : "Go to Location");
            setDescription(locationName ? `Go to ${locationName}` : "Go to Location");
        }
    }

    // handle employee selection from dropdown
    const handleEmployeeChange = (selectedOption) => {
        dispatch(setAdhocTaskEmployee(selectedOption?.value || null));
    }

    // format employees for select dropdown
    const getEmployeeOptions = () => {
        if (!allEmployees) return [];

        return allEmployees.map(employee => {
            const capitalizedName = employee.title
                ? employee.title.charAt(0).toUpperCase() + employee.title.slice(1)
                : employee.title;
            return {
                value: employee,
                label: `${capitalizedName} - ${employee.role || employee.employeeId}`
            };
        });
    }

    // get selected employee option for dropdown
    const getSelectedEmployeeOption = () => {
        if (!adhocTaskEmployee) return null;

        const capitalizedName = adhocTaskEmployee.title
            ? adhocTaskEmployee.title.charAt(0).toUpperCase() + adhocTaskEmployee.title.slice(1)
            : adhocTaskEmployee.title;

        return {
            value: adhocTaskEmployee,
            label: `${capitalizedName} - ${adhocTaskEmployee.role || adhocTaskEmployee.employeeId}`
        };
    }

    //#endregion

    //#region quick time helpers

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 30000);
        return () => clearInterval(interval);
    }, []);

    const formatTime = (date) => {
        return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
    };

    const roundToNearest5 = (date) => {
        const result = new Date(date);
        result.setMinutes(Math.round(date.getMinutes() / 5) * 5, 0, 0);
        return result;
    };

    const getNext5Min = (date) => {
        const result = new Date(date);
        const remainder = date.getMinutes() % 5;
        result.setMinutes(remainder === 0 ? date.getMinutes() : date.getMinutes() + (5 - remainder), 0, 0);
        return result;
    };

    const nowRounded = formatTime(roundToNearest5(currentTime));
    const quickTimes = useMemo(() => {
        const first = getNext5Min(currentTime);
        const second = new Date(first); second.setMinutes(second.getMinutes() + 5);
        const third = new Date(second); third.setMinutes(third.getMinutes() + 5);
        return [formatTime(first), formatTime(second), formatTime(third)];
    }, [currentTime]);

    const minutesUntilTask = useMemo(() => {
        if (!assignedTime) return null;
        const [hours, minutes] = assignedTime.split(':').map(Number);
        const taskStart = new Date(currentTime);
        taskStart.setHours(hours, minutes, 0, 0);
        return Math.round((taskStart - currentTime) / 60000);
    }, [assignedTime, currentTime]);

    //#endregion

    return (
        <div style={{ padding: Spacing.SCALE_12 }}>

            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: Spacing.SCALE_12, marginBottom: Spacing.SCALE_22 }}>

                <div style={{ flex: 1 }}>
                    {adhocTaskEmployee && (
                        <div style={{ width: '80px' }}>
                            <EmployeeProfilePicture
                                employee={adhocTaskEmployee}
                                showName={true}
                                showRole={true}
                                name={adhocTaskEmployee?.title}
                                role={adhocTaskEmployee?.role}
                            />
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button variant="outline-secondary" onClick={props.onClose}>
                        Cancel
                    </Button>
                    {!Object.isObjectEmpty(selectedAdhocTask) ?
                        <Button variant="outline-primary" onClick={() => editAdhocTask()} disabled={!assignedTime || !title || !adhocTaskEmployee || showSuccess} style={{ minWidth: '90px' }}>
                            {updateTaskStatus == "loading" ? <CustomSpinner size={"small"} variant="light" /> : "Edit Task"}
                        </Button>
                        :
                        <Button variant="outline-primary" onClick={createAdhocTask} disabled={!assignedTime || !title || !adhocTaskEmployee || showSuccess} style={{ minWidth: '150px' }}>
                            {createTaskStatus == "loading" ? <CustomSpinner size={"small"} variant="light" /> : "Create Adhoc Task"}
                        </Button>
                    }
                </div>
            </div>

            <CustomDivider noMargin={true} />

            {/* success message */}
            {showSuccess && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#d4edda',
                    border: '1px solid #c3e6cb',
                    borderRadius: '6px',
                    marginTop: Spacing.SCALE_12,
                    marginBottom: Spacing.SCALE_12,
                }}>
                    <FiCheckCircle size={20} color="#28a745" />
                    <span style={{ color: '#155724', fontWeight: '500' }}>Adhoc task assigned successfully!</span>
                </div>
            )}

            {/* error message */}
            {showError && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    borderRadius: '6px',
                    marginTop: Spacing.SCALE_12,
                    marginBottom: Spacing.SCALE_12,
                }}>
                    <FiAlertCircle size={20} color="#dc3545" />
                    <span style={{ color: '#721c24', fontWeight: '500' }}>
                        {createTaskError || 'Failed to assign adhoc task. Please try again.'}
                    </span>
                </div>
            )}

            {/* form */}
            <div style={{ marginTop: Spacing.SCALE_22 }}>

                {/* Row 1: Select Employee (full width) */}
                <Row style={{ marginBottom: Spacing.SCALE_22 }}>
                    <Col xs={12}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Select Employee *</label>
                        <Select
                            options={getEmployeeOptions()}
                            value={getSelectedEmployeeOption()}
                            onChange={handleEmployeeChange}
                            placeholder="Choose an employee..."
                            isClearable={true}
                            styles={{
                                control: (provided) => ({ ...provided, backgroundColor: 'white', borderColor: '#ced4da', minHeight: '38px' }),
                                placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
                                singleValue: (provided) => ({ ...provided, color: '#495057' }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? '#f8f9fa' : 'white', color: '#495057', cursor: 'pointer' }),
                            }}
                        />
                    </Col>
                </Row>

                {/* Row 2: Task Type (left) + Location (right) */}
                <Row style={{ marginBottom: Spacing.SCALE_22 }}>
                    <Col xs={12} md={6}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Task Type *</label>
                        <Select
                            options={ADHOC_TASK_OPTIONS}
                            value={title.startsWith("Go to") ? { value: title, label: title } : (ADHOC_TASK_OPTIONS.find(opt => opt.value === title) || null)}
                            onChange={(selectedOption) => handleTitleChange(selectedOption)}
                            placeholder="Select task type..."
                            isClearable={true}
                            styles={{
                                control: (provided) => ({ ...provided, backgroundColor: 'white', borderColor: '#ced4da', minHeight: '38px' }),
                                placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
                                singleValue: (provided) => ({ ...provided, color: '#495057' }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? '#f8f9fa' : 'white', color: '#495057', cursor: 'pointer' }),
                            }}
                        />
                    </Col>
                    <Col xs={12} md={6}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Location <span style={{ fontStyle: 'italic', fontWeight: '400' }}>(Optional)</span></label>
                        <Select
                            options={(locations || []).map(loc => ({ value: loc, label: loc.title || loc.name || '' }))}
                            value={selectedLocation ? { value: selectedLocation, label: selectedLocation.title || selectedLocation.name || '' } : null}
                            onChange={handleLocationChange}
                            placeholder="Select location..."
                            isClearable={true}
                            styles={{
                                control: (provided) => ({ ...provided, backgroundColor: 'white', borderColor: '#ced4da', minHeight: '38px' }),
                                placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
                                singleValue: (provided) => ({ ...provided, color: '#495057' }),
                                menu: (provided) => ({ ...provided, zIndex: 9999 }),
                                option: (provided, state) => ({ ...provided, backgroundColor: state.isFocused ? '#f8f9fa' : 'white', color: '#495057', cursor: 'pointer' }),
                            }}
                        />
                    </Col>
                </Row>

                {/* Row 3: Assigned Time dropdown + Quick Set buttons inline */}
                <Row style={{ marginBottom: Spacing.SCALE_12 }}>
                    <Col xs={12}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Assigned Time *</label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Form.Control
                                as="select"
                                value={assignedTime}
                                onChange={(event) => setAssignedTime(event.target.value)}
                                required
                                style={{ width: 'auto' }}
                            >
                                <option value="">Select time...</option>
                                {timeIntervalOptions.map((time) => (
                                    <option key={time} value={time}>{time}</option>
                                ))}
                            </Form.Control>
                            <Button variant={assignedTime === nowRounded ? "secondary" : "outline-secondary"} size="sm" onClick={() => setAssignedTime(formatTime(roundToNearest5(new Date())))}>
                                Now ({nowRounded})
                            </Button>
                            <Button variant={assignedTime === quickTimes[0] ? "secondary" : "outline-secondary"} size="sm" onClick={() => setAssignedTime(quickTimes[0])}>
                                {quickTimes[0]}
                            </Button>
                            <Button variant={assignedTime === quickTimes[1] ? "secondary" : "outline-secondary"} size="sm" onClick={() => setAssignedTime(quickTimes[1])}>
                                {quickTimes[1]}
                            </Button>
                            <Button variant={assignedTime === quickTimes[2] ? "secondary" : "outline-secondary"} size="sm" onClick={() => setAssignedTime(quickTimes[2])}>
                                {quickTimes[2]}
                            </Button>
                        </div>
                    </Col>
                </Row>

                {/* time until task callout */}
                {assignedTime && minutesUntilTask !== null && (
                    <div style={{
                        backgroundColor: minutesUntilTask < 0 ? '#fff3cd' : '#d4edda',
                        padding: Spacing.SCALE_12,
                        borderRadius: '6px',
                        marginBottom: Spacing.SCALE_12,
                        border: `1px solid ${minutesUntilTask < 0 ? '#ffc107' : '#c3e6cb'}`,
                    }}>
                        <p style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            margin: 0,
                            color: minutesUntilTask < 0 ? '#856404' : '#155724',
                        }}>
                            {minutesUntilTask < 0
                                ? `Task start is ${Math.abs(minutesUntilTask)} minute${Math.abs(minutesUntilTask) !== 1 ? 's' : ''} in the past`
                                : minutesUntilTask === 0
                                    ? 'Task starts now'
                                    : `Task starts in ${minutesUntilTask} minute${minutesUntilTask !== 1 ? 's' : ''}`
                            }
                        </p>
                    </div>
                )}

                <Row className="mt-3">
                    <Col xs={12}>
                        <label style={{ fontSize: '14px', fontWeight: '500', color: 'grey', marginBottom: '6px', display: 'block' }}>Description</label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={description}
                            placeholder="Enter task description (optional)"
                            onChange={(event) => setDescription(event.target.value)}
                            maxLength={500}
                        />
                    </Col>
                </Row>

            </div>
        </div>
    )
}

export default CreateAdhocTask